import React, { useEffect, useState } from 'react'
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown'
import { formatPrice } from '@aytm/app/swidget/base/questions/Ecommerce/functions.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects.ts'
import CardAbout from './mobile/CardAbout.tsx'
import SubscribeCoupon from './SubscribeCoupon.tsx'
import commonStyle from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/common.module.scss'
import style from './amazon.module.scss'

interface DeliveryPanelProps {
  schema: any
  questionSchema: any
  qty: number
  findability: boolean
  randomInDate: Date
  randomDate: Date | null
  onAddToCart: (qty: number, subscribe?: number) => void
  onBuyNow: (qty: number) => void
}

const DeliveryPanel = ({
  schema,
  questionSchema,
  qty,
  findability,
  randomInDate,
  randomDate,
  onAddToCart,
  onBuyNow
}: DeliveryPanelProps) => {
  const viewport = useViewport()
  const timeline = useTimeline()
  const { t, languageTag } = useSurveyLocale()
  const [isSubscribe, setIsSubscribe] = useState(false)
  const [subscribeFrequency, setSubscribeFrequency] = useState<number>(2)
  const [ddValue, setDdValue] = useState(1)

  const options = [...Array(questionSchema.max_qty_per_product || 5).keys()].map(i => {
    return { id: i + 1, label: `${t('questions', 'ec', 'amazon', 'qty')} ${(i + 1).toString()}`, value: i + 1 }
  })

  useEffect(() => {
    setDdValue(1)
  }, [isSubscribe])

  useEffect(() => {
    setIsSubscribe(false)
  }, [schema.id])

  const overLimit = questionSchema.max_qty_per_product > 0 && ddValue + qty > questionSchema.max_qty_per_product

  const onDropdownChange = (v: DropdownChangeParams) => {
    setDdValue(v.value as number)
  }

  const hasSubscribe = Boolean(schema.subscribe_percent)
  const subscribeDiscount = schema.subscribe_percent || 0
  const basePrice = schema.discounted_price || schema.price
  const subscribePrice = basePrice * (1 - schema.subscribe_percent / 100)

  // Helper functions
  const getDeliveryTiming = () => {
    if (1 == schema.prime) return `${t('questions', 'ec', 'amazon', 'today')}, `
    if (2 == schema.prime) return `${t('questions', 'ec', 'amazon', 'tomorrow')}, `
    return ''
  }

  const formatDeliveryDate = (date: Date) => {
    return date.toLocaleDateString(languageTag, {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    })
  }

  const frequencyOptions = [
    { label: t('questions', 'ec', 'amazon', 'oneWeek'), value: 1 },
    { label: t('questions', 'ec', 'amazon', 'twoWeeks'), value: 2 },
    { label: t('questions', 'ec', 'amazon', 'threeWeeks'), value: 3 },
    { label: t('questions', 'ec', 'amazon', 'oneMonth'), value: 4 },
    { label: t('questions', 'ec', 'amazon', 'twoMonths'), value: 8 },
    { label: t('questions', 'ec', 'amazon', 'threeMonths'), value: 12 }
  ]

  const isButtonDisabled = (findability && 1 === qty) || overLimit

  const deliveryContent = (
    <>
      {hasSubscribe && viewport.mobile ? (
        <>
          <div className={style.waysToBuyHeader}>
            {t('questions', 'ec', 'amazon', 'waysToBuy')}{' '}
            <strong>{t('questions', 'ec', 'amazon', isSubscribe ? 'subscribeAndSave' : 'oneTimePurchase')}</strong>
          </div>
          <div className={style.waysToBuyOptions}>
            <div
              className={`${style.waysToBuyCard} ${!isSubscribe ? style.waysToBuyCardActive : ''}`}
              onClick={() => {
                setIsSubscribe(false)
                timeline.log({
                  type: 'subscribe_toggle',
                  ref: schema.id,
                  target: 'false'
                })
              }}
            >
              <div className={style.waysToBuyCardTitle}>{t('questions', 'ec', 'amazon', 'oneTimePurchase')}</div>
              <div className={style.waysToBuyCardPrice}>
                <span
                  className={style.waysToBuyPrice}
                  dangerouslySetInnerHTML={{
                    __html: formatPrice(basePrice, questionSchema.price_mask, true, true)
                  }}
                />
                {schema.per_count_price ? (
                  <span className={style.waysToBuyPerCount}>({schema.per_count_price})</span>
                ) : null}
              </div>
            </div>
            <div
              className={`${style.waysToBuyCard} ${isSubscribe ? style.waysToBuyCardActive : ''}`}
              onClick={() => {
                setIsSubscribe(true)
                timeline.log({
                  type: 'subscribe_toggle',
                  ref: schema.id,
                  target: 'true'
                })
                hapticEffects.selection()
              }}
            >
              <div className={style.waysToBuyCardTitle}>
                {t('questions', 'ec', 'amazon', 'subscribeAndSave')} <span className={style.subscribeIcon}></span>
              </div>
              <div className={style.waysToBuyCardPrice}>
                <span
                  className={style.waysToBuyPrice}
                  dangerouslySetInnerHTML={{
                    __html: formatPrice(subscribePrice, questionSchema.price_mask, true, true)
                  }}
                />
                {schema.per_count_price ? (
                  <span className={style.waysToBuyPerCount}>({schema.per_count_price})</span>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}

      {viewport.mobile && schema.red_label ? (
        <div className={commonStyle.redLabel} dangerouslySetInnerHTML={{ __html: schema.red_label }} />
      ) : null}
      <div className={style.price}>
        {/* {schema.subscribe_percent ? <span className={style.percent}>-{schema.subscribe_percent}%</span> : null} */}
        <span
          className={style.bigPrice}
          dangerouslySetInnerHTML={{
            __html: formatPrice(
              schema.discounted_price ? schema.discounted_price : schema.price,
              questionSchema.price_mask,
              true,
              true
            )
          }}
        ></span>
      </div>
      {hasSubscribe && isSubscribe ? (
        <>
          <div className={style.oneTimePrice}>
            {t('questions', 'ec', 'amazon', 'oneTimePrice')}:{' '}
            <strong
              dangerouslySetInnerHTML={{
                __html: formatPrice(basePrice, questionSchema.price_mask)
              }}
            />
          </div>
          {schema.subscribe_repeat_percent ? (
            <div className={style.subscribeSavings}>
              {t<(subscribeDiscount: number, repeatPercent: number) => string>(
                'questions',
                'ec',
                'amazon',
                'subscribeSavingsText'
              )(subscribeDiscount, schema.subscribe_repeat_percent || 0)}
            </div>
          ) : null}
          <ul className={style.subscribeBullets}>
            <EmptyLink>
              <li>{t('questions', 'ec', 'amazon', 'noFees')} </li>
            </EmptyLink>
            <EmptyLink>
              <li>{t('questions', 'ec', 'amazon', 'cancelAnytime')}</li>
            </EmptyLink>
          </ul>
          {schema.subscribe_coupon_percent ? <SubscribeCoupon couponPercent={schema.subscribe_coupon_percent} /> : null}
        </>
      ) : null}
      <span className={style.base}>
        <span
          className={style.bigPrice}
          dangerouslySetInnerHTML={{
            __html: t('questions', 'ec', 'amazon', 'getShip')
          }}
        />
        <EmptyLink>
          <span className={style.link}>{t('questions', 'ec', 'amazon', 'prime')}</span>
        </EmptyLink>
      </span>
      <EmptyLink>
        <span className={`${commonStyle.arrowDown} ${commonStyle.link} ${style.freeReturns}`}>
          <span>{t('questions', 'ec', 'amazon', 'freeReturns')}</span>
          <i />
        </span>
      </EmptyLink>
      <EmptyLink>
        <span className={style.base}>
          <span className={style.link}>{t('questions', 'ec', 'amazon', 'freeDelivery')}&nbsp;</span>
          {getDeliveryTiming()}
          <b>{formatDeliveryDate(randomInDate)}</b>
        </span>
      </EmptyLink>
      {!schema.prime && randomDate ? (
        <span className={style.base}>
          {t('questions', 'ec', 'amazon', 'fastDelivery')}&nbsp;
          <b>{formatDeliveryDate(randomDate)}</b>
        </span>
      ) : null}
      <EmptyLink>
        <div className={style.deliveryTo}>
          <div className={style.deliveryToIcon}></div>
          <p>
            {t('questions', 'ec', 'amazon', 'deliverTo')} {t('questions', 'ec', 'amazon', 'myAddress')}
          </p>
        </div>
      </EmptyLink>
      {questionSchema.max_qty_per_product > 0 ? (
        <span className={style.leftInStock}>
          {t<(left: number) => string>('questions', 'ec', 'amazon', 'leftInStock')(questionSchema.max_qty_per_product)}
        </span>
      ) : (
        <span className={style.inStock}>{t('questions', 'ec', 'amazon', 'inStock')}</span>
      )}

      {!findability && (
        <div id={style.dropdown}>
          <Dropdown
            options={options}
            confirmKeyboardSelect={true}
            onChange={onDropdownChange}
            value={ddValue}
            disabled={!options.length}
            placeholder={t('questions', 'ec', 'amazon', 'qty')}
          />
        </div>
      )}

      {!isSubscribe ? (
        <>
          <button
            disabled={isButtonDisabled}
            className={`${style.add} ${isButtonDisabled ? style.disabled : ''}`}
            onClick={event => {
              if (!isButtonDisabled) {
                hapticEffects.selection()
                event?.stopPropagation()
                onAddToCart(ddValue)
              }
            }}
          >
            {t('questions', 'ec', 'amazon', 'addToCart')}
          </button>
          <button
            disabled={isButtonDisabled}
            className={`${style.buy} ${isButtonDisabled ? style.disabled : ''}`}
            onClick={event => {
              if (!isButtonDisabled) {
                event?.stopPropagation()
                onBuyNow(ddValue)
              }
            }}
          >
            {t('questions', 'ec', 'amazon', 'buyNow')}
          </button>
        </>
      ) : (
        <>
          <button
            disabled={isButtonDisabled}
            className={`${style.add} ${isButtonDisabled ? style.disabled : ''}`}
            onClick={event => {
              if (!isButtonDisabled) {
                hapticEffects.selection()
                event?.stopPropagation()
                onAddToCart(ddValue, subscribeFrequency)
              }
            }}
          >
            {t('questions', 'ec', 'amazon', 'subscribe')}
          </button>
          <div className={style.subscribeFrequency}>
            <span className={style.subscribeFrequencyLabel}>{t('questions', 'ec', 'amazon', 'deliverEvery')}</span>
            <Dropdown
              options={frequencyOptions}
              value={subscribeFrequency}
              onChange={(e: DropdownChangeParams) => setSubscribeFrequency(e.value)}
              className={style.subscribeFrequencyDropdown}
            />
          </div>
        </>
      )}

      <span className={`${commonStyle.arrowDown} ${commonStyle.link} ${style.seeMore}`}>
        <EmptyLink>
          <i />
          <span>{t('questions', 'ec', 'amazon', 'seeMore')}</span>
        </EmptyLink>
      </span>
    </>
  )

  return (
    <>
      {!hasSubscribe ? (
        <div className={style.deliveryHeader}>
          <EmptyLink>
            <span className={style.deliveryActive}>{t('questions', 'ec', 'amazon', 'delivery')}</span>
          </EmptyLink>
          <EmptyLink>
            <span>{t('questions', 'ec', 'amazon', 'pickup')}</span>
          </EmptyLink>
        </div>
      ) : null}
      {viewport.mobile && hasSubscribe ? (
        <div className={style.deliveryHeaderMobile}>
          <span className={style.deliveryHeaderTitle}>{t('questions', 'ec', 'amazon', 'purchaseOptions')}</span>
        </div>
      ) : null}
      <div className={`${style.delivery} ${hasSubscribe && !viewport.mobile ? style.deliverySubscribe : ''}`}>
        {hasSubscribe && !viewport.mobile ? (
          <div className={style.subscribeOptions}>
            <div className={`${style.subscribeOption} ${!isSubscribe ? style.subscribeOptionActive : ''}`}>
              <div
                className={style.subscribeOptionHeader}
                onClick={() => {
                  setIsSubscribe(false)
                  timeline.log({
                    type: 'subscribe_toggle',
                    ref: schema.id,
                    target: 'false'
                  })
                }}
              >
                <div className={style.subscribeOptionContent}>
                  <div className={style.subscribeOptionLabel}>{t('questions', 'ec', 'amazon', 'oneTimePurchase')}</div>
                </div>
                <div className={`${style.subscribeRadio} ${!isSubscribe ? style.subscribeRadioActive : ''}`} />
              </div>

              {!isSubscribe ? (
                <div className={style.subscribeOptionBody}>{deliveryContent}</div>
              ) : (
                <div className={`${style.subscribeOptionPrice}`}>
                  <div
                    className={style.bigPrice}
                    dangerouslySetInnerHTML={{
                      __html: formatPrice(basePrice, questionSchema.price_mask, true, true)
                    }}
                  />
                  <div className={style.base} style={{ display: 'block' }}>
                    {getDeliveryTiming()}
                    <b>
                      {t('questions', 'ec', 'amazon', 'freeDelivery')}&nbsp;
                      {formatDeliveryDate(randomInDate)} {t('questions', 'ec', 'amazon', 'to')}{' '}
                      {t('questions', 'ec', 'amazon', 'myAddress')}
                    </b>
                  </div>
                </div>
              )}
            </div>

            <div className={`${style.subscribeOption} ${isSubscribe ? style.subscribeOptionActive : ''}`}>
              <div
                className={style.subscribeOptionHeader}
                onClick={() => {
                  setIsSubscribe(true)
                  timeline.log({
                    type: 'subscribe_toggle',
                    ref: schema.id,
                    target: 'true'
                  })
                  hapticEffects.selection()
                }}
              >
                <div className={style.subscribeOptionContent}>
                  <div className={style.subscribeOptionLabel}>
                    {t('questions', 'ec', 'amazon', 'subscribeAndSave')} <span className={style.subscribeIcon}></span>
                  </div>
                </div>
                <div className={`${style.subscribeRadio} ${isSubscribe ? style.subscribeRadioActive : ''}`} />
              </div>

              {isSubscribe ? (
                <div className={style.subscribeOptionBody}>
                  <div className={style.price}>
                    <div
                      className={style.bigPrice}
                      dangerouslySetInnerHTML={{
                        __html: formatPrice(subscribePrice, questionSchema.price_mask, true, true)
                      }}
                    />
                  </div>
                  {schema.subscribe_repeat_percent ? (
                    <div className={style.subscribeSavings}>
                      {t<(subscribeDiscount: number, repeatPercent: number) => string>(
                        'questions',
                        'ec',
                        'amazon',
                        'subscribeSavingsText'
                      )(subscribeDiscount, schema.subscribe_repeat_percent)}
                    </div>
                  ) : null}
                  <ul className={style.subscribeBullets}>
                    <EmptyLink>
                      <li>{t('questions', 'ec', 'amazon', 'noFees')} </li>
                    </EmptyLink>
                    <EmptyLink>
                      <li>{t('questions', 'ec', 'amazon', 'cancelAnytime')}</li>
                    </EmptyLink>
                  </ul>
                  <EmptyLink>
                    <span className={style.subscribeLearnMore}>{t('questions', 'ec', 'amazon', 'learnMore')}</span>
                  </EmptyLink>

                  <EmptyLink>
                    <span className={style.base}>
                      <span className={style.link}>{t('questions', 'ec', 'amazon', 'freeDelivery')}&nbsp;</span>
                      {getDeliveryTiming()}
                      <b>{formatDeliveryDate(randomInDate)}</b>
                    </span>
                  </EmptyLink>
                  {questionSchema.max_qty_per_product > 0 ? (
                    <span className={style.leftInStock}>
                      {t<(left: number) => string>(
                        'questions',
                        'ec',
                        'amazon',
                        'leftInStock'
                      )(questionSchema.max_qty_per_product)}
                    </span>
                  ) : (
                    <span className={style.inStock}>{t('questions', 'ec', 'amazon', 'inStock')}</span>
                  )}
                  {!findability && (
                    <div id={style.dropdown}>
                      <Dropdown
                        options={options}
                        confirmKeyboardSelect={true}
                        onChange={onDropdownChange}
                        value={ddValue}
                        disabled={!options.length}
                        placeholder={t('questions', 'ec', 'amazon', 'qty')}
                      />
                    </div>
                  )}
                  <button
                    disabled={isButtonDisabled}
                    className={`${style.add} ${isButtonDisabled ? style.disabled : ''}`}
                    onClick={event => {
                      if (!isButtonDisabled) {
                        hapticEffects.selection()
                        event?.stopPropagation()
                        onAddToCart(ddValue, subscribeFrequency)
                      }
                    }}
                  >
                    {t('questions', 'ec', 'amazon', 'subscribe')}
                  </button>

                  <div className={style.subscribeFrequency}>
                    <span className={style.subscribeFrequencyLabel}>
                      {t('questions', 'ec', 'amazon', 'deliverEvery')}
                    </span>
                    <Dropdown
                      options={frequencyOptions}
                      value={subscribeFrequency}
                      onChange={(e: DropdownChangeParams) => setSubscribeFrequency(e.value)}
                      className={style.subscribeFrequencyDropdown}
                    />
                  </div>

                  <EmptyLink>
                    <div className={style.subscribeInfo}>
                      <span className={style.subscribeInfoLabel}>{t('questions', 'ec', 'amazon', 'subscription')}</span>
                      <span className={style.subscribeInfoText}>
                        {t('questions', 'ec', 'amazon', 'subscriptionInfo')}
                      </span>
                    </div>
                  </EmptyLink>
                </div>
              ) : (
                <div className={`${style.subscribeOptionPrice}`}>
                  <div
                    className={style.bigPrice}
                    dangerouslySetInnerHTML={{
                      __html: formatPrice(subscribePrice, questionSchema.price_mask, true, true)
                    }}
                  />
                  <div className={style.base} style={{ display: 'block' }}>
                    {getDeliveryTiming()}
                    <b>
                      {t('questions', 'ec', 'amazon', 'freeDelivery')}&nbsp;
                      {formatDeliveryDate(randomInDate)} {t('questions', 'ec', 'amazon', 'to')}{' '}
                      {t('questions', 'ec', 'amazon', 'myAddress')}
                    </b>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          deliveryContent
        )}

        {viewport.mobile && <CardAbout schema={schema} />}
      </div>

      {!viewport.mobile && hasSubscribe ? (
        <>
          <div className={style.deliveryActions}>
            <EmptyLink>
              <button className={style.deliveryAction}>{t('questions', 'ec', 'amazon', 'addToAutoBuy')}</button>
            </EmptyLink>
            <EmptyLink>
              <button className={style.deliveryAction}>{t('questions', 'ec', 'amazon', 'addToList')}</button>
            </EmptyLink>
          </div>
        </>
      ) : null}
    </>
  )
}

export default DeliveryPanel
